import type { SharedSelection } from '@/components/ui';
import { Input, Select, SelectItem } from '@/components/ui';
import type { BasicSelectProps } from './types';
import { makeOnChangeHandler } from './helpers';
import { useGroups } from '@/store/people/hooks';
import { useMemo, useState } from 'react';
import {
  getGroupAlias,
  getGroupProperty,
} from '@/components/people/structure/utils';
import type { GroupTypeKey } from '@/shared/types/group';
import { type MinistryTypeKey } from '@/shared/types/ministry';
import { Icon } from '../icon';
import { strictlyCompact } from '@/lib/compact';
import { MinistryStructure } from '@/store/org/types';

export interface Props extends BasicSelectProps {
  value?: string | string[];
  campus?: string | string[];
  type?: GroupTypeKey;
  defaultLabel?: boolean;
  parent?: string;
  onChangeMultiple?: (ids: string[]) => void;
  onChangeSingle?: (id: string) => void;
  isReadOnly?: boolean;
}

interface TypeEntry {
  key: '' | GroupTypeKey;
  ministry: '' | MinistryTypeKey;
  label: string;
  plural: string;
  icon?: string;
}

export function GroupSelect({
  campus,
  type,
  parent,
  defaultLabel,
  label,
  placeholder,
  startContent,
  multiple,
  value,
  isDisabled,
  isReadOnly,
  onChange,
  onChangeMultiple,
  onChangeSingle,
  className,
}: Props) {
  const { list: items, lookup } = useGroups(campus, type, parent);
  const onChangeHandler = makeOnChangeHandler({
    anyTarget: onChange,
    multiple,
    multipleTarget: onChangeMultiple,
    singleTarget: onChangeSingle,
  });
  const selected = Array.isArray(value) ? value : strictlyCompact([value]);
  const labelText = useMemo(() => {
    if (label) {
      return label;
    }

    if (defaultLabel && type) {
      return getGroupAlias(type, 'type');
    }

    return undefined;
  }, [label, type, defaultLabel]);
  const ariaLabel = label || 'Item';

  if (isReadOnly) {
    const display = selected
      .map((id) => lookup[id]?.data.title)
      .filter((name) => name?.length)
      .join(',');

    return (
      <Input
        isReadOnly
        label={label}
        value={display}
        className={className}
        placeholder='Unassigned'
      />
    );
  }

  return (
    <Select
      label={labelText}
      aria-label={ariaLabel}
      startContent={startContent}
      placeholder={placeholder}
      isDisabled={isDisabled}
      selectionMode={multiple ? 'multiple' : 'single'}
      onChange={onChangeHandler}
      defaultSelectedKeys={selected}
      className={className}
    >
      {items.map((item) => (
        <SelectItem key={item.id}>{item.data.title}</SelectItem>
      ))}
    </Select>
  );
}

export function TypeSelect({
  label,
  ministry,
  value = '',
  onChange,
  className,
  icons,
  placeholder,
  plural,
}: {
  label?: string;
  ministry?: '' | MinistryTypeKey;
  value?: '' | GroupTypeKey;
  onChange: (type: '' | GroupTypeKey) => void;
  className?: string;
  icons?: boolean;
  placeholder?: string;
  plural?: boolean;
}) {
  const [selected, setSelected] = useState<'' | GroupTypeKey>('');
  const changeHandler = (key: SharedSelection) => {
    if (!key) {
      setSelected('');
      onChange('');
    } else if (key instanceof Set) {
      const [sel] = key;
      let value = sel as '' | GroupTypeKey;
      value ??= '';
      setSelected(value);
      onChange(value);
    }
  };

  const visibleItems = items.filter(
    (item) => !item.ministry || item.ministry === ministry,
  );
  const startContent =
    selected === '' ? undefined : (
      <Icon icon={getGroupProperty(selected, 'icon')} />
    );

  return (
    <Select
      label={label}
      aria-label='Select Type'
      items={visibleItems}
      defaultSelectedKeys={[value]}
      onSelectionChange={changeHandler}
      className={className}
      startContent={startContent}
      placeholder={placeholder}
    >
      {visibleItems.map((item) => (
        <SelectItem
          key={item.key}
          startContent={
            icons && item.icon ? <Icon icon={item.icon} width={20} /> : <></>
          }
        >
          {plural ? item.plural : item.label}
        </SelectItem>
      ))}
    </Select>
  );
}

function makeTypeEntry(
  key: GroupTypeKey,
  ministry: MinistryTypeKey,
): TypeEntry {
  return {
    key,
    ministry,
    label: getGroupAlias(key, 'type'),
    plural: getGroupAlias(key, 'type', true),
    icon: getGroupProperty(key, 'icon'),
  };
}

const items: TypeEntry[] = [
  { key: '', ministry: '', label: 'Any Type', plural: 'Any Type' },
  ...Object.entries(MinistryStructure).flatMap(([_, structures]) =>
    (structures || []).map((structure) =>
      makeTypeEntry(structure.type, structure.ministry as MinistryTypeKey),
    ),
  ),
];
